import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';

function CheckinModal({ screen, currentUser, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    qualityCheckPassed: true,
    qualityNotes: '',
    coveringChanged: false,
    coveredPositionsCount: screen.coveredPositionsCount || 0,
    coveringType: screen.coveringType || '',
    coveringNotes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.qualityCheckPassed && !formData.qualityNotes.trim()) {
      setError('Please provide quality notes when quality check fails');
      return;
    }

    if (formData.coveringChanged && !formData.coveringNotes.trim()) {
      setError('Please provide covering notes when covering has changed');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/silkscreens/${screen.id}/checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          qualityCheckPassed: formData.qualityCheckPassed,
          qualityNotes: formData.qualityNotes,
          coveringChanged: formData.coveringChanged,
          coveredPositionsCount: formData.coveringChanged ? parseInt(formData.coveredPositionsCount) : undefined,
          coveringType: formData.coveringChanged ? formData.coveringType : undefined,
          coveringNotes: formData.coveringChanged ? formData.coveringNotes : undefined,
          checkedInBy: currentUser?.id || 'system'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to check in screen');
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
      }}>
        <h2 style={{
          fontSize: '1.75rem',
          fontWeight: '700',
          color: '#4CAF50',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          📥 Check In Screen
        </h2>

        <div style={{
          background: '#e8f5e9',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '24px',
          fontSize: '0.9rem',
          color: '#2e7d32'
        }}>
          <strong>{screen.screenCode}</strong> - {screen.currentCustomerName}
          <br />
          {screen.currentItemDescription}
          <br />
          <span style={{ fontSize: '0.85rem', color: '#558b2f' }}>
            Status: {screen.status === 'in_production' ? 'In Production' : 'At Supplier'}
          </span>
        </div>

        {error && (
          <div style={{
            background: '#ffebee',
            color: '#c62828',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Quality Check */}
          <div style={{
            background: '#f5f5f5',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h3 style={{
              fontSize: '1.1rem',
              fontWeight: '600',
              color: '#333',
              marginBottom: '12px'
            }}>
              Quality Check
            </h3>
            
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                name="qualityCheckPassed"
                checked={formData.qualityCheckPassed}
                onChange={handleChange}
                style={{
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontWeight: '600', color: '#333' }}>
                Quality check passed
              </span>
            </label>

            <div style={{ marginTop: '12px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#333',
                fontSize: '0.95rem'
              }}>
                Quality Notes {!formData.qualityCheckPassed && '*'}
              </label>
              <textarea
                name="qualityNotes"
                value={formData.qualityNotes}
                onChange={handleChange}
                placeholder={formData.qualityCheckPassed ? 'Optional notes about screen condition' : 'Describe quality issues found'}
                rows="3"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  transition: 'border-color 0.2s',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
                onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>
          </div>

          {/* Covering Status */}
          <div style={{
            background: '#f5f5f5',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            <h3 style={{
              fontSize: '1.1rem',
              fontWeight: '600',
              color: '#333',
              marginBottom: '12px'
            }}>
              Covering Status
            </h3>
            
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                name="coveringChanged"
                checked={formData.coveringChanged}
                onChange={handleChange}
                style={{
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontWeight: '600', color: '#333' }}>
                Covering has changed
              </span>
            </label>

            {formData.coveringChanged && (
              <>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#333',
                    fontSize: '0.95rem'
                  }}>
                    Number of Covered Positions *
                  </label>
                  <input
                    type="number"
                    name="coveredPositionsCount"
                    value={formData.coveredPositionsCount}
                    onChange={handleChange}
                    min="0"
                    max={screen.totalPositions}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  />
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#333',
                    fontSize: '0.95rem'
                  }}>
                    Covering Type
                  </label>
                  <select
                    name="coveringType"
                    value={formData.coveringType}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      transition: 'border-color 0.2s',
                      cursor: 'pointer'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  >
                    <option value="">Select type...</option>
                    <option value="temporary">Temporary</option>
                    <option value="permanent">Permanent</option>
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: '#333',
                    fontSize: '0.95rem'
                  }}>
                    Covering Notes *
                  </label>
                  <textarea
                    name="coveringNotes"
                    value={formData.coveringNotes}
                    onChange={handleChange}
                    placeholder="Describe which positions are covered and why"
                    rows="3"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      transition: 'border-color 0.2s',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                    onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                  />
                </div>
              </>
            )}
          </div>

          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '12px 24px',
                background: '#f5f5f5',
                color: '#666',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#e0e0e0')}
              onMouseLeave={(e) => e.target.style.background = '#f5f5f5'}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 24px',
                background: loading ? '#b0bec5' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#43A047')}
              onMouseLeave={(e) => !loading && (e.target.style.background = '#4CAF50')}
            >
              {loading ? 'Checking In...' : 'Check In Screen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CheckinModal;
